/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { noop } from "lodash"
import { FC, useState } from "react"
import { Spin, Row, Col, Alert } from "@duxton-web/ui"
import {
  AddImageUniversal,
  EditableFilled,
  TrashCanFilled,
  VerticalDragUniversal,
  WarningAltFilled,
} from "@duxton-web/icons"
import { ImageWithFetchRetry } from "components/ImageWithFetchRetry"
import { useTranslation, useTranslationsMap } from "common/utils/i18n"
import { ConfirmModal } from "components/common/ConfirmModal"

import { SortableHandle } from "react-sortable-hoc"
import {
  thumbnailItemPhoto,
  addPhotoStyle,
  thumbnailItemPhotoError,
  uploadPhotoErrorStyle,
  thumbnailItemPhotoWarning,
  uploadPhotoWarningStyle,
} from "./styles"

export interface ItemDetailPhotoProps {
  src: string
  onClickEdit(): void
  onClickAdd(): void
  onClickDelete(): void
  isInProgress?: boolean
  // GFMEX-8204 deprecated - error-display is moved into PhotoUploadDrawer.tsx
  error?: string
  // GFMEX-8204 deprecated - warning-display is moved into PhotoUploadDrawer.tsx
  warning?: string
  disabled: boolean
  className?: string
  showDragger?: boolean
}

const cursorPointerStyle = css`
  cursor: pointer;
`

const DragHandle = SortableHandle(() => (
  <div>
    <VerticalDragUniversal className="drag-btn" />
  </div>
))

export const ItemDetailPhoto: FC<ItemDetailPhotoProps> = ({
  onClickEdit,
  onClickAdd,
  onClickDelete,
  src,
  isInProgress,
  error,
  warning,
  disabled,
  className,
  showDragger,
}: ItemDetailPhotoProps) => {
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false)

  const handleConfirmDelete = () => {
    setShowConfirmDelete(false)
    onClickDelete()
  }

  const textMap = useTranslationsMap({
    addPhoto: "menu.drawers.item.buttons.addPhoto",
    alertError: "menu.item.modal.issueWithThisPhoto",
    tapToLearn: "menu.item.modal.tapToLearn",
    thereIsIssueWithPhoto: "menu.item.modal.thereIsIssueWithPhoto",
  })

  let photoThumbnail = null
  if (!isInProgress && !src) {
    photoThumbnail = (
      <div
        css={css`
          ${addPhotoStyle}
          ${!disabled && cursorPointerStyle}
        `}
        onClick={!disabled ? onClickAdd : noop}
        data-testid="photothumbnail-no-src"
      >
        <div>
          <AddImageUniversal />
          <p>{textMap.addPhoto}</p>
        </div>
      </div>
    )
  } else if (isInProgress) {
    photoThumbnail = (
      <div
        css={css`
          ${addPhotoStyle}
          ${!disabled && cursorPointerStyle}
        `}
        data-testid="photothumbnail-in-progress"
      >
        <div>
          <Spin />
        </div>
      </div>
    )
  } else if (error) {
    photoThumbnail = (
      <div css={thumbnailItemPhotoError} data-testid="photothumbnail-error">
        <ImageWithFetchRetry
          className="thumbnail-img"
          src={src}
          width={130}
          height={130}
          onClick={!disabled ? onClickEdit : noop}
        />
        {!disabled && (
          <WarningAltFilled
            className="error-sign"
            onClick={!disabled ? onClickEdit : noop}
          />
        )}
        {!disabled && (
          <div className="action-wrapper">
            <TrashCanFilled
              className="delete-btn"
              onClick={!disabled ? () => setShowConfirmDelete(true) : noop}
            />
          </div>
        )}
      </div>
    )
  } else if (warning) {
    photoThumbnail = (
      <div css={thumbnailItemPhotoWarning} data-testid="photothumbnail-warning">
        <ImageWithFetchRetry
          className="thumbnail-img"
          src={src}
          width={130}
          height={130}
          onClick={!disabled ? onClickEdit : noop}
        />
        <WarningAltFilled
          className="warning-sign"
          onClick={!disabled ? onClickEdit : noop}
        />
        {!disabled && (
          <div className="action-wrapper">
            <EditableFilled
              className="pencil-btn"
              onClick={!disabled ? onClickEdit : noop}
            />
            <TrashCanFilled
              className="delete-btn"
              onClick={!disabled ? () => setShowConfirmDelete(true) : noop}
            />
          </div>
        )}
      </div>
    )
  } else if (!isInProgress && src) {
    photoThumbnail = (
      <div
        css={thumbnailItemPhoto}
        data-testid="photothumbnail-not-in-progress"
      >
        <ImageWithFetchRetry
          className="thumbnail-img"
          src={src}
          width={130}
          height={130}
        />
        {!disabled && (
          <div className="action-wrapper">
            <EditableFilled
              className="pencil-btn"
              onClick={!disabled ? onClickEdit : noop}
            />
            <TrashCanFilled
              className="delete-btn"
              onClick={!disabled ? () => setShowConfirmDelete(true) : noop}
            />
            {showDragger && <DragHandle data-testid="drag-handle" />}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={className}>
      {error && (
        <Row css={uploadPhotoErrorStyle} data-testid="item-detail-photo-error">
          <Col span={24}>
            <Alert
              message={textMap.alertError}
              description={textMap.tapToLearn}
              type="error"
            />
          </Col>
        </Row>
      )}
      {warning && (
        <Row
          css={uploadPhotoWarningStyle}
          data-testid="item-detail-photo-warning"
        >
          <Col span={24}>
            <Alert
              message={`${textMap.thereIsIssueWithPhoto}`}
              description={warning}
              type="warning"
            />
          </Col>
        </Row>
      )}
      <Row>
        <Col span={24}>{photoThumbnail}</Col>
      </Row>
      <ConfirmModal
        title={useTranslation("menu.item.confirm.deleteImage")}
        content={useTranslation("menu.item.confirm.actionCannotUndone")}
        visible={showConfirmDelete}
        onCancel={() => setShowConfirmDelete(false)}
        onConfirm={handleConfirmDelete}
        data-testid="confirm-modal"
      />
    </div>
  )
}
